/**
 * HTTP-клиент.
 *
 * Три вещи, которые здесь неочевидны и потому зафиксированы:
 *
 * 1. Access-токен живёт ТОЛЬКО в памяти, refresh — в localStorage. XSS-угон
 *    короткоживущего access из JS-памяти невозможен без исполнения кода в том
 *    же контексте, а refresh всё равно нужен между перезагрузками WebView.
 * 2. Refresh при 401 выполняется ровно один раз: параллельные запросы,
 *    получившие 401 одновременно, ждут общий promise, иначе получаем
 *    N ротаций refresh-токена и гарантированный разлогин (ротация одноразовая).
 * 3. Ретраится только GET и только на сетевой ошибке. Мутация может быть уже
 *    применена сервером — повтор join/withdrawal недопустим.
 *
 * Логирование: в лог не попадают ни токены, ни адреса кошельков, ни тела
 * запросов. Только метод, путь и код ошибки.
 */

import type { ApiErrorBody, TokenPair } from './types';

const BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api';
const REFRESH_STORAGE_KEY = 'sg.refresh';
export const DELETED_ACCOUNT_STORAGE_KEY = 'sg.deleted-account';
const TIMEOUT_MS = 15_000;

/** Событие на window: слушает useAuth и сбрасывает состояние в 'anon'. */
export const AUTH_LOGOUT_EVENT = 'auth:logout';
/** Событие на window: токены обновились (полезно для дебага/аналитики). */
export const AUTH_REFRESHED_EVENT = 'auth:refreshed';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly context: Record<string, unknown> | undefined;

  constructor(
    code: string,
    status: number,
    message?: string,
    context?: Record<string, unknown>,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.context = context;
  }

  /** true для ошибок, которые не имеет смысла ретраить. */
  get isFatal(): boolean {
    return this.status >= 400 && this.status < 500 && this.status !== 408 && this.status !== 429;
  }
}

/* -------------------------------------------------------------------------- */
/* Хранилище токенов                                                          */
/* -------------------------------------------------------------------------- */

let accessToken: string | null = null;

function readRefresh(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    // Приватный режим / отключённый storage: работаем без персистентности.
    return null;
  }
}

function writeRefresh(token: string | null): void {
  try {
    if (token === null) window.localStorage.removeItem(REFRESH_STORAGE_KEY);
    else window.localStorage.setItem(REFRESH_STORAGE_KEY, token);
  } catch {
    /* no-op */
  }
}

export const tokenStore = {
  getAccess(): string | null {
    return accessToken;
  },
  getRefresh(): string | null {
    return readRefresh();
  },
  hasRefresh(): boolean {
    return readRefresh() !== null;
  },
  setPair(pair: Pick<TokenPair, 'access_token' | 'refresh_token'>): void {
    accessToken = pair.access_token;
    writeRefresh(pair.refresh_token);
  },
  setAccess(token: string | null): void {
    accessToken = token;
  },
  clear(): void {
    accessToken = null;
    writeRefresh(null);
  },
};

/* -------------------------------------------------------------------------- */
/* Низкоуровневый запрос                                                      */
/* -------------------------------------------------------------------------- */

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  /** Query-параметры; null/undefined отбрасываются. */
  query?: Record<string, QueryValue>;
  /** Внешний signal (TanStack Query передаёт свой). */
  signal?: AbortSignal;
  /** Не подставлять Authorization (публичные и auth-эндпоинты). */
  anonymous?: boolean;
  /** Не пытаться refresh при 401 (сам refresh-запрос). */
  noRefresh?: boolean;
  /** Таймаут, мс. */
  timeoutMs?: number;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs === '' ? url : `${url}?${qs}`;
}

function statusToCode(status: number): string {
  if (status === 400) return 'validation_failed';
  if (status === 401) return 'token_expired';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 422) return 'validation_failed';
  if (status === 429) return 'rate_limited';
  return 'generic';
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false;
  const error = (value as { error?: unknown }).error;
  if (typeof error !== 'object' || error === null) return false;
  return typeof (error as { code?: unknown }).code === 'string';
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (isApiErrorBody(body)) {
    return new ApiError(
      body.error.code,
      response.status,
      body.error.message,
      body.error.context,
    );
  }
  return new ApiError(statusToCode(response.status), response.status);
}

/**
 * Объединяет внешний signal с внутренним таймаутом. AbortSignal.any доступен
 * не во всех WebView, поэтому руками.
 */
function withTimeout(
  external: AbortSignal | undefined,
  timeoutMs: number,
): { signal: AbortSignal; dispose: () => void } {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(new DOMException('timeout', 'TimeoutError')), timeoutMs);

  const onExternalAbort = (): void => controller.abort(external?.reason);
  if (external) {
    if (external.aborted) controller.abort(external.reason);
    else external.addEventListener('abort', onExternalAbort, { once: true });
  }

  return {
    signal: controller.signal,
    dispose: () => {
      window.clearTimeout(timer);
      external?.removeEventListener('abort', onExternalAbort);
    },
  };
}

async function rawRequest<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body: unknown,
  options: RequestOptions,
): Promise<T> {
  const { signal, dispose } = withTimeout(options.signal, options.timeoutMs ?? TIMEOUT_MS);
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (!options.anonymous && accessToken !== null) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      credentials: 'omit',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });

    if (!response.ok) throw await toApiError(response);
    if (response.status === 204) return undefined as T;

    const text = await response.text();
    if (text === '') return undefined as T;
    return JSON.parse(text) as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error instanceof DOMException && error.name === 'AbortError') {
      // Отмена снаружи (unmount, смена экрана) не должна выглядеть как сбой сети.
      if (options.signal?.aborted) throw error;
      throw new ApiError('network', 0, 'timeout');
    }
    throw new ApiError('network', 0);
  } finally {
    dispose();
  }
}

/* -------------------------------------------------------------------------- */
/* Refresh с дедупликацией                                                    */
/* -------------------------------------------------------------------------- */

let refreshPromise: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  const refresh = readRefresh();
  if (refresh === null) return false;
  try {
    const pair = await rawRequest<TokenPair>(
      'POST',
      '/auth/refresh',
      { refresh_token: refresh },
      { anonymous: true, noRefresh: true },
    );
    tokenStore.setPair(pair);
    window.dispatchEvent(new CustomEvent(AUTH_REFRESHED_EVENT));
    return true;
  } catch {
    return false;
  }
}

/** Один общий promise на все параллельные 401 — ротация refresh одноразовая. */
export function refreshTokens(): Promise<boolean> {
  if (refreshPromise === null) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function emitLogout(): void {
  tokenStore.clear();
  window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT));
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const idempotent = method === 'GET';

  try {
    return await rawRequest<T>(method, path, body, options);
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;

    // Сеть отвалилась: GET можно повторить один раз, мутацию — нет.
    if (error.code === 'network' && idempotent) {
      return await rawRequest<T>(method, path, body, options);
    }

    if (error.status === 401 && !options.anonymous && !options.noRefresh) {
      const refreshed = await refreshTokens();
      if (!refreshed) {
        emitLogout();
        throw error;
      }
      return await rawRequest<T>(method, path, body, { ...options, noRefresh: true });
    }

    throw error;
  }
}

export const api = {
  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('GET', path, undefined, options);
  },
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('POST', path, body ?? {}, options);
  },
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PATCH', path, body ?? {}, options);
  },
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PUT', path, body ?? {}, options);
  },
  del<T = void>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('DELETE', path, body, options);
  },
};

/*
 * Скачивание отчётов идёт НЕ через этот клиент: в Telegram WebView загрузка по
 * fetch с Authorization-заголовком не работает. Сервер выдаёт подписанную
 * ссылку с коротким сроком (POST /admin/export/link), её открывает внешний
 * браузер — см. AnalyticsScreen.
 */

/** Полный выход: гасит токены и уведомляет стор. Сетевую ошибку игнорирует. */
export async function logoutRequest(): Promise<void> {
  const refresh = readRefresh();
  if (refresh !== null) {
    try {
      await api.post<void>('/auth/logout', { refresh_token: refresh }, { noRefresh: true });
    } catch {
      /* выход должен работать и офлайн */
    }
  }
  emitLogout();
}

/** Ключ i18n для ошибки: errors.<code> с фолбэком на errors.generic. */
export function errorCodeOf(error: unknown): string {
  if (error instanceof ApiError) return error.code;
  return 'generic';
}
